import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';

export interface SousCategorie {
  id: number;
  nom: string;
  categorie_id: number;
  ordre: number;
}

export interface Categorie {
  id: number;
  nom: string;
  profile: string;
  ordre: number;
  sousCategories?: SousCategorie[];
}

export const useCategories = () => {
  return useQuery<Categorie[]>({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await apiClient.get('/categories');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCategoriesWithSubcategories = () => {
  return useQuery<Categorie[]>({
    queryKey: ['categories', 'with-subcategories'],
    queryFn: async () => {
      const response = await apiClient.get('/categories/with-subcategories');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateCategorie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { nom: string; profile?: string; ordre?: number }) => {
      const response = await apiClient.post('/categories', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};

export const useUpdateCategorie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Categorie> }) => {
      const response = await apiClient.patch(`/categories/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};

export const useDeleteCategorie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};

export const useCreateSousCategorie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ categorieId, data }: { categorieId: number; data: { nom: string; ordre?: number } }) => {
      const response = await apiClient.post(`/categories/${categorieId}/sous-categories`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};

export const useUpdateSousCategorie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { nom?: string; ordre?: number } }) => {
      const response = await apiClient.patch(`/categories/sous-categories/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};

export const useDeleteSousCategorie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/categories/sous-categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};
